/**
 * Exit breadcrumbs — a persistent forensic trail for programmatic exits from
 * an active game.
 *
 * Why: the "kicked to Home with no Rejoin" incident happened on a phone with
 * no attached debugger. localStorage survives the session, so the NEXT time it
 * happens the breadcrumb can be read on the affected device via
 * window.__DOMINO_DIAGNOSTICS__.dumpExitHistory().
 *
 * Instrumentation only: recording never throws, never blocks, and changes no
 * behavior. Ring buffer capped at MAX_ENTRIES.
 */

import { getAuth } from 'firebase/auth';

export type GameExitReason =
  | 'session_abandoned'
  | 'session_finished'
  | 'game_aborted'
  | 'reconnect_failed'
  | 'network_fetch_failed'
  | 'auth_invalid'
  | 'game_channel_rejected'
  | 'explicit_leave_match'
  | 'leave_and_save_bot'
  | 'end_solo_bot_game'
  | 'forfeit_match'
  | 'unknown';

export interface GameExitBreadcrumb {
  ts:         string;   // ISO timestamp
  reason:     GameExitReason;
  note?:      string;   // which code path recorded it
  route:      string;   // location.pathname at record time
  gameId:     number | null;
  sessionId:  number | null;
  wsState:    string;   // 'connected' | 'disconnected' | 'unknown' (as reported by caller)
  authState:  string;   // 'signed_in' | 'signed_out' | 'unknown'
  online:     boolean;
  visibility: string;   // document.visibilityState
  url:        string;
  userAgent:  string;
}

const STORAGE_KEY = 'domino_exit_breadcrumbs';
const MAX_ENTRIES = 20;

function readRaw(): GameExitBreadcrumb[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Best-effort Firebase auth probe — read-only, never throws.
 *  getAuth() throws if no default app is initialized → 'unknown'. */
function probeAuthState(): string {
  try {
    return getAuth().currentUser ? 'signed_in' : 'signed_out';
  } catch {
    return 'unknown';
  }
}

/**
 * Record one breadcrumb immediately before a programmatic navigation away
 * from an active game. Safe to call from anywhere; never throws.
 */
export function recordGameExit(
  reason: GameExitReason,
  ctx: {
    gameId?:      number;
    sessionId?:   number;
    wsConnected?: boolean;
    note?:        string;
  } = {},
): void {
  try {
    const entry: GameExitBreadcrumb = {
      ts:         new Date().toISOString(),
      reason,
      note:       ctx.note,
      route:      window.location.pathname,
      gameId:     ctx.gameId ?? null,
      sessionId:  ctx.sessionId ?? null,
      wsState:    ctx.wsConnected === undefined ? 'unknown' : (ctx.wsConnected ? 'connected' : 'disconnected'),
      authState:  probeAuthState(),
      online:     typeof navigator !== 'undefined' ? navigator.onLine : true,
      visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
      url:        window.location.href,
      userAgent:  typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    };
    const entries = readRaw();
    entries.push(entry);
    while (entries.length > MAX_ENTRIES) entries.shift();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    // Also echo to console so live sessions show the exit reason inline.
    console.warn(`[ExitBreadcrumb] ${reason}${ctx.note ? ` (${ctx.note})` : ''} game=${entry.gameId} session=${entry.sessionId} ws=${entry.wsState} online=${entry.online} vis=${entry.visibility}`);
  } catch {
    // Diagnostics must never break the app.
  }
}

/** Read-only copy of the stored breadcrumb history (oldest first). */
export function readGameExitHistory(): GameExitBreadcrumb[] {
  return readRaw();
}
